import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useMemo, useRef, useState } from 'react'
import { Loader2, Check, Clock, CalendarDays } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { phFormat, peso, manilaDayIso } from '@/lib/ph'

export const Route = createFileRoute('/book/$slug')({ component: PublicBooking })

interface BookingCompany {
  name: string | null; logo: string | null; accent_color: string | null
  phone: string | null; timezone: string; suburb: string | null; state: string | null
}
interface BookingService {
  id: string; name: string; description: string | null; duration_min: number; price: number | null
}

const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x }

function PublicBooking() {
  const { slug } = Route.useParams()
  const [service, setService] = useState<BookingService | null>(null)
  const [slot, setSlot] = useState<string | null>(null)
  // Choosing a time has to move the customer to the form. Up to thirty days of slots sit between
  // them, so on a phone a tap looks like nothing happened and the booking is abandoned.
  const detailsRef = useRef<HTMLParagraphElement>(null)
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', message: '' })
  const [done, setDone] = useState<{ when: string } | null>(null)
  const [err, setErr] = useState<string | null>(null)
  // Spam trap: a real customer never fills this, a naive bot fills everything.
  const honeypot = useRef('')
  // Stable per-attempt id so a double-tap or a retry cannot create two bookings.
  const requestId = useRef(crypto.randomUUID())

  const company = useQuery({
    queryKey: ['book-company', slug],
    queryFn: async (): Promise<BookingCompany | null> => {
      const { data, error } = await supabase.rpc('public_booking_company', { p_slug: slug })
      if (error) throw error
      return data as BookingCompany | null
    },
  })

  const services = useQuery({
    queryKey: ['book-services', slug],
    queryFn: async (): Promise<BookingService[]> => {
      const { data, error } = await supabase.rpc('public_booking_services', { p_slug: slug })
      if (error) throw error
      return (data ?? []) as BookingService[]
    },
    enabled: !!company.data,
  })

  const from = useMemo(() => new Date(), [])
  const to = useMemo(() => addDays(from, 21), [from])

  const availability = useQuery({
    queryKey: ['book-avail', slug, service?.id ?? ''],
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase.rpc('public_booking_availability', {
        p_slug: slug, p_from: isoDay(from), p_to: isoDay(to), p_service: service?.id ?? null,
      })
      if (error) throw error
      return (data ?? []) as string[]
    },
    enabled: !!service,
  })

  const book = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('public_create_booking', {
        p_slug: slug, p_slot: slot, p_service: service?.id ?? null,
        p_name: form.name, p_email: form.email || null, p_phone: form.phone || null,
        p_address: form.address || null, p_message: form.message || null,
        p_request_id: requestId.current,
      })
      if (error) throw error
      return data as { ok: boolean; error?: string; when?: string }
    },
    onSuccess: (r) => {
      if (!r.ok) {
        setErr(r.error ?? 'Could not book that time.')
        // The slot is gone; make them pick again from fresh data rather than retrying a dead slot.
        setSlot(null)
        availability.refetch()
        return
      }
      setDone({ when: r.when ?? slot! })
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Something went wrong.'),
  })

  if (company.isLoading) {
    return <div className="flex min-h-screen items-center justify-center bg-canvas-subtle"><Loader2 className="h-6 w-6 animate-spin text-token-muted" /></div>
  }
  if (!company.data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas-subtle px-4 text-center">
        <div>
          <p className="font-display text-[20px] font-bold text-token-primary">Online booking isn’t available</p>
          <p className="mt-1 text-[14px] text-token-secondary">This business isn’t taking bookings through this link.</p>
        </div>
      </div>
    )
  }

  const co = company.data
  const accent = co.accent_color || '#0038a8'
  const name = (co.name || 'This business').trim()
  const tz = co.timezone

  const fmtDay = (iso: string) =>
    phFormat(new Date(iso), 'date', { weekday: 'short', day: 'numeric', month: 'short', timeZone: tz })
  const fmtTime = (iso: string) =>
    phFormat(new Date(iso), 'time', { hour: 'numeric', minute: '2-digit', timeZone: tz })

  // Slots arrive as UTC instants; group them by the BUSINESS's local day, not the visitor's.
  const byDay = new Map<string, string[]>()
  for (const s of availability.data ?? []) {
    const key = manilaDayIso(s)
    if (!byDay.has(key)) byDay.set(key, [])
    byDay.get(key)!.push(s)
  }

  if (done) {
    return (
      <div className="min-h-screen bg-canvas-subtle px-4 py-12">
        <div className="mx-auto max-w-md rounded-[16px] border border-token-subtle bg-token-surface px-6 py-10 text-center">
          <Check className="mx-auto h-9 w-9" style={{ color: accent }} />
          <p className="mt-3 font-display text-[22px] font-bold tracking-[-0.02em] text-token-primary">You’re booked in</p>
          <p className="mt-1.5 text-[15px] text-token-secondary">
            {fmtDay(done.when)} at <span className="font-semibold text-token-primary">{fmtTime(done.when)}</span>
          </p>
          <p className="mt-4 text-[13px] text-token-secondary">
            {name} will be in touch to confirm. {co.phone && <>Questions? Call <a className="font-semibold text-token-primary" href={`tel:${co.phone.replace(/[^\d+]/g, '')}`}>{co.phone}</a>.</>}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-canvas-subtle px-4 py-8 sm:py-12">
      <div className="mx-auto max-w-md">
        <div className="mb-5 flex items-center gap-2.5">
          {co.logo
            ? <img src={co.logo} alt={name} className="h-10 w-10 rounded-[10px] object-cover" />
            : <span className="flex h-10 w-10 items-center justify-center rounded-[10px] text-[17px] font-bold text-white" style={{ background: accent }}>{name[0]?.toUpperCase()}</span>}
          <div>
            <p className="font-display text-[18px] font-bold tracking-[-0.02em] text-token-primary">{name}</p>
            {(co.suburb || co.state) && <p className="text-[12px] text-token-secondary">{[co.suburb, co.state].filter(Boolean).join(', ')}</p>}
          </div>
        </div>

        <div className="rounded-[16px] border border-token-subtle bg-token-surface p-5">
          {/* 1 — service */}
          <p className="text-[11px] font-black uppercase tracking-[0.14em] text-token-secondary">What do you need?</p>
          <div className="mt-3 space-y-2">
            {services.isLoading && <div className="h-14 animate-pulse rounded-[10px] bg-canvas-subtle" />}
            {services.data?.length === 0 && !services.isLoading && (
              <p className="text-[13px] text-token-secondary">This business hasn’t listed any bookable services yet.</p>
            )}
            {services.data?.map((s) => (
              <button
                key={s.id}
                onClick={() => { setService(s); setSlot(null); setErr(null) }}
                className={`w-full rounded-[10px] border px-3.5 py-3 text-left transition-colors ${
                  service?.id === s.id ? 'border-token-strong bg-token-surface' : 'border-token-subtle hover:bg-token-surface'
                }`}
              >
                <span className="flex items-center justify-between gap-3">
                  <span className="min-w-0">
                    <span className="block truncate text-[14px] font-semibold text-token-primary">{s.name}</span>
                    {s.description && <span className="block truncate text-[12.5px] text-token-secondary">{s.description}</span>}
                  </span>
                  <span className="flex-none text-right">
                    {s.price != null && (
                      <span className="block text-[14px] font-bold text-token-primary">
                        {Number(s.price) === 0 ? 'Free' : peso(s.price, { whole: true })}
                      </span>
                    )}
                    <span className="block text-[11.5px] text-token-secondary">{s.duration_min} min</span>
                  </span>
                </span>
              </button>
            ))}
          </div>

          {/* 2 — time */}
          {service && (
            <>
              <p className="mt-6 text-[11px] font-black uppercase tracking-[0.14em] text-token-secondary">Pick a time</p>
              {availability.isLoading ? (
                <div className="mt-3 h-20 animate-pulse rounded-[10px] bg-canvas-subtle" />
              ) : byDay.size === 0 ? (
                <p className="mt-3 flex items-center gap-2 text-[13px] text-token-secondary">
                  <CalendarDays className="h-4 w-4" /> No times available in the next few weeks — give them a call instead.
                </p>
              ) : (
                <div className="mt-3 space-y-4">
                  {[...byDay.entries()].map(([day, slots]) => (
                    <div key={day}>
                      <p className="text-[12.5px] font-semibold text-token-secondary">{fmtDay(slots[0])}</p>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {slots.map((s) => (
                          <button
                            key={s}
                            onClick={() => {
                              setSlot(s); setErr(null)
                              requestAnimationFrame(() =>
                                detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
                            }}
                            className={`rounded-[8px] border px-3 py-1.5 text-[13px] font-semibold transition-colors ${
                              slot === s ? 'border-token-strong bg-brand text-brand-fg' : 'border-token-subtle text-token-primary hover:bg-token-surface'
                            }`}
                          >
                            {fmtTime(s)}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* 3 — details */}
          {slot && (
            <>
              <p ref={detailsRef} className="mt-6 scroll-mt-4 text-[11px] font-black uppercase tracking-[0.14em] text-token-secondary">Your details</p>
              <div className="mt-3 space-y-2.5">
                <input className={inputCls} placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input className={inputCls} placeholder="Mobile" inputMode="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                <input className={inputCls} placeholder="Email (optional)" inputMode="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <input className={inputCls} placeholder="Address of the job" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                <textarea className={inputCls + ' min-h-[76px]'} placeholder="What needs doing?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                {/* Honeypot — visually hidden, never announced, never focusable. */}
                <input
                  tabIndex={-1} autoComplete="off" aria-hidden="true"
                  className="absolute h-0 w-0 opacity-0"
                  onChange={(e) => { honeypot.current = e.target.value }}
                />
              </div>

              {err && <p className="mt-3 rounded-[8px] bg-token-danger-surface px-3 py-2 text-[13px] font-medium text-token-danger">{err}</p>}

              <button
                className="mt-4 w-full rounded-[10px] py-3 text-[15px] font-bold text-white disabled:opacity-60"
                style={{ background: accent }}
                disabled={book.isPending || !form.name.trim() || !form.phone.trim()}
                onClick={() => {
                  if (honeypot.current) { setDone({ when: slot }); return } // silently absorb bots
                  setErr(null); book.mutate()
                }}
              >
                {book.isPending ? 'Booking…' : `Book ${fmtTime(slot)}`}
              </button>
              <p className="mt-2 flex items-center justify-center gap-1.5 text-[11.5px] text-token-secondary">
                <Clock className="h-3.5 w-3.5" /> Times shown in {tz.split('/')[1]?.replace('_', ' ') ?? tz}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const inputCls =
  'w-full rounded-[10px] border border-token-subtle bg-token-surface px-3.5 py-2.5 text-[14px] text-token-primary outline-none placeholder:text-token-muted focus:border-token-strong'
